import { Component, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { SampleTrackingStorage } from '../../sample-tracking-storage';
import { SampleEQueryData, SampleSearchData } from '../interfaces/sample-search';
import { GridDetails, GridData, GridEvent } from '../interfaces/grid';
import { TrackingOverviewService } from '../service/tracking-overview.service';
import { VisitDetails } from '../interfaces/visit-data';
import { SponsorStudy } from '../../../sponsor-dashboard/sponsor-study';
import { map, concatMap } from 'rxjs/operators';
import { Observable, forkJoin } from 'rxjs';
import { UrlBuilder } from '../../../core';
import { ActivityLogger } from '../../../activities/activity-logger';
import { IBreadcrumb, BreadcrumbService } from '../../shared/breadcrumb-service/breadcrumb.service';
import * as moment from 'moment';

@Component({
    selector: 'app-visit-overview',
    template: require('./visit-overview.component.html'),
    styles: [require('./visit-overview.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VisitOverviewComponent {

    public sampleSearchData: SampleSearchData;
    public gridDetails: GridDetails;
    public visitDetails: VisitDetails;
    public isLoadingCompleted = false;
    public isSampleOnHold = false;
    public eQueryDetails: SampleEQueryData;
    public equeryOpenDate: string = moment().subtract(1, 'days').format('YYYY-MM-DD');

    constructor(
        private sampleTrackingStorage: SampleTrackingStorage,
        private changeDectRef: ChangeDetectorRef,
        private urlBuilder: UrlBuilder,
        private trackingService: TrackingOverviewService,
        private activityLogger: ActivityLogger,
        private breadcrumbService: BreadcrumbService
    ) {
        if (!this.sampleTrackingStorage.sampleSearchData) {
            this.urlBuilder.esa().sampletracking().home().navigate();
            return;
        }

        this.sampleTrackingStorage.sampleSearchData
            .pipe(
                map((sampleSearch: SampleSearchData) => {
                    this.sampleSearchData = sampleSearch;
                    return sampleSearch;
                }),
                concatMap((ss) =>
                    forkJoin([
                        this.sampleTrackingStorage.getReportData(ss.accessionNumber),
                        this.sampleTrackingStorage.getVisitData(ss.accessionNumber),
                        this.sampleTrackingStorage.getSampleHeaderDetails(ss.accessionNumber),
                        this.sampleTrackingStorage.getOpenEqueryQuery(ss.accessionNumber),
                    ]),
                ),
                map(([gridData, visitData, sampleData, equeryData]) => {
                    let routeData: IBreadcrumb = {
                        label: 'Visit ' + this.sampleSearchData.accessionNumber,
                        url: this.urlBuilder.esa().sampletracking().visitDetails().url(),
                        data: this.sampleSearchData
                    };
                    this.breadcrumbService.AddRouterToBreadCrumb(routeData);
                    this.isLoadingCompleted = true;
                    let formatedData: GridData[] = [];

                    if (gridData && gridData.sampleAliquot && gridData.sampleAliquot.length) {
                        const childrenData = gridData.sampleAliquot.filter((sa: GridData) => sa.parentBarcode) as GridData[];
                        const parentData = gridData.sampleAliquot.filter((sa: GridData) => !sa.parentBarcode) as GridData[];
                        this.sampleSearchData.sampleCount = gridData.sampleAliquot.length;
                        this.isSampleOnHold = (gridData.sampleAliquot.filter((x: GridData) => x.isOnSMHold) as GridData[]).length > 0 ? true : false;

                        formatedData = gridData.sampleAliquot.map((gd: GridData) => {
                            gd.cssClass = '';
                            gd.aliquotCreatedDate = gd.parentAliquotDate ||  gd.childAliquotDate;
                            gd = this.setSMHoldData(gd);
                            gd.expandData = { child: [], parent: [] };

                            childrenData.map((cd: GridData) => {
                                if ((cd.barcode && gd.parentBarcode) && cd.barcode === gd.parentBarcode) { // condition to handel grand parent
                                    cd.aliquotCreatedDate = cd.childAliquotDate;
                                    gd.expandData.parent.push(cd);
                                    gd.cssClass += ' expand';
                                }

                                if ((cd.parentBarcode && gd.barcode) && cd.parentBarcode === gd.barcode) {
                                    cd.aliquotCreatedDate = cd.childAliquotDate;
                                    gd.expandData.child.push(cd);
                                    gd.cssClass += ' expand';
                                }
                            });

                            parentData.map((pa: GridData) => {
                                if ((pa.barcode && gd.parentBarcode) && pa.barcode === gd.parentBarcode) {
                                    pa.aliquotCreatedDate = pa.parentAliquotDate;
                                    gd.expandData.parent.push(pa);
                                    gd.cssClass += ' expand';
                                }
                            });
                            return gd;
                        });
                    }


                    this.gridDetails = { header: this.trackingService.sampleAliquotsHeaderData, data: formatedData };

                    if (visitData && visitData.visitDetail) {
                        const data = visitData.visitDetail;
                        data.investigatorNameAndNo = data.investigatorName + ' / ' + data.investigatorNumber;
                        data.visitNameAndPvc = data.visitName + ' / ' + data.protocolVisitCode;

                        if (data.isAgeProject) {
                            data.displayAge = (data.age && data.age.value && data.age.unit) ? data.age.value + ' ' + data.age.unit : '';
                        } else {
                            data.displayAge = (data.birthDate) ? moment(data.birthDate).local().format('DD-MMM-YYYY') : '';
                        }

                        this.visitDetails = { header: this.trackingService.visitDetailsHeading, data };

                    }

                    if (sampleData && sampleData.eQueryOpenHoldModel) {
                        this.eQueryDetails = sampleData.eQueryOpenHoldModel;
                    }

                    if (equeryData && equeryData.equeryOpenData && equeryData.equeryOpenData.equeryOpenDate) {
                        this.equeryOpenDate = moment(equeryData.equeryOpenData.equeryOpenDate).subtract(1, 'days').format('YYYY-MM-DD');
                    }

                    this.changeDectRef.markForCheck();
                }),
            )
            .subscribe();

        activityLogger.logActivity('Access Sample Tracking Visit Details');
    }

    public onClickCreateReport() {
        const fileNameToDownload = 'Visit Tracking Details ' + this.sampleSearchData.accessionNumber;
        this.trackingService.createReportForVisit(this.gridDetails, this.visitDetails, fileNameToDownload);
    }

    public onGridItemClick(selectedItem: GridEvent) {
        const data = {
            accessionNumber: selectedItem.data.accessionNumber,
            containerNumber: selectedItem.data.containerNumber,
            currentSampleStatus: selectedItem.data.currentSampleStatus
        };

        this.sampleTrackingStorage.sampleSearchData = new Observable(ob => ob.next(data));

        this.urlBuilder
            .esa()
            .sampletracking()
            .sampleDetails(data.containerNumber)
            .navigate();
    }

    public onEQueryButtonClick() {
        let sponsorStudy: SponsorStudy = new SponsorStudy();
        sponsorStudy.protocolId = this.eQueryDetails.protocolFK;
        const toDate = moment().local().format('YYYY-MM-DD');
        
        const eqUrl = this.urlBuilder
            .esa()
            .sponsor()
            .study(sponsorStudy)
            .activityReports()
            .documents('equery')
            .getUrl({ queryParams: { 'date': `${this.equeryOpenDate}To${toDate}`, 'accessionNumber': this.eQueryDetails.accessionNumber } });
        window.open(eqUrl);
    }

    private setSMHoldData(data: GridData) {
        if (!data.isOnSMHold) { return data; }
        data.smToolTip = data.isOnSMHold ? 'SM hold; contact RSC for details.' : null;
        data.cssClass += ' sm-row';
        return data;
    }
}